import { defaultConferenceLocalStorageSchema, defaultConferenceSessionStorageSchema, FieldErrorList } from "./types";
import moment, { Moment } from "moment";
import { IconName } from "@fortawesome/fontawesome-common-types";
import * as Bowser from "bowser";
import { DebugSettings, defaultDebugSettings } from "./components/debug/types";
import { RefObject, useEffect, useRef, useState } from "react";
import { compareVersions } from "compare-versions";
import { defineMessage, defineMessages, MessageDescriptor } from "react-intl";
import { FieldError, ValueErrorType } from "./api";
import { createSettingsStoreContext, createStorageStore, objectKeys } from "@dvrt/react-tools";
import { defaultClientSettings } from "@dvrt/web-phone-client";

export function copy<T>(data: T): T {
    return JSON.parse(JSON.stringify(data));
}

export function extractFieldErrors(errors: FieldErrorList, fieldName: string, pathIndex: number = 0): FieldErrorList {
    const res = [] as FieldErrorList;
    for (const error of errors) {
        if (error.path[pathIndex] === fieldName) res.push(error);
    }
    return res;
}

const VALUE_ERROR_TYPE_TO_MESSAGE = defineMessages<ValueErrorType>({
    POSITIVE_DATE_RANGE: { id: "POSITIVE_DATE_RANGE", defaultMessage: "Negative date range not allowed" },
    FUTURE_DATE: { id: "FUTURE_DATE", defaultMessage: "Must not be in past" },
    PIN_CODE: { id: "PIN_CODE", defaultMessage: "Only numbers are allowed" },
    POSITIVE_NUMBER: { id: "POSITIVE_NUMBER", defaultMessage: "Must be greater than 0" },
    STRING_MIN_LENGTH: {
        id: "STRING_MIN_LENGTH",
        defaultMessage:
            "Must be at least {limitValue, number} {limitValue, plural, one {character} other {characters} }",
    },
    EMAIL: { id: "EMAIL", defaultMessage: "Not a valid email address" },
    NOT_EXISTS: { id: "NOT_EXISTS", defaultMessage: "Does not exist" },
    PHONE_NUMBER: { id: "PHONE_NUMBER", defaultMessage: "Not a valid E164 or SIP uri" },
    ONE_OF_EMAIL_NAME_PHONE_NUMBER_REQUIRED: {
        id: "ONE_OF_EMAIL_NAME_PHONE_NUMBER_REQUIRED",
        defaultMessage: "At least one required",
    },
    REQUIRED: { id: "REQUIRED", defaultMessage: "Required" },
    NOT_DELETABLE: { id: "NOT_DELETABLE", defaultMessage: "This meeting cannot be deleted" },
    MEETING_NOT_IN_FUTURE: { id: "MEETING_NOT_IN_FUTURE", defaultMessage: "Cannot delete already started meeting" },
    MEETING_NOT_ONGOING: { id: "MEETING_NOT_ONGOING", defaultMessage: "Meeting is not ongoing" },
});

export function fieldErrorToMessage(error: FieldError): MessageDescriptor {
    return (
        VALUE_ERROR_TYPE_TO_MESSAGE[error.data.key] ||
        defineMessage({ id: "error.unexpected", defaultMessage: "Unexpected error!" })
    );
}

export function createUtcMoment(datetimeString: string): Moment {
    return moment(moment.parseZone(datetimeString)).utc();
}

export function createLocalMomentFromUTC(date: Moment): Moment {
    const localDate = date.clone();
    return localDate.local();
}

export type MomentDateParts = {
    year: number;
    month: number;
    date: number;
};

export type MomentTimeParts = {
    hour: number;
    minute: number;
};

export function parseDateStringForMoment(date: string): MomentDateParts {
    const parts = date.split("-");
    return {
        year: parseInt(parts[0]),
        month: parseInt(parts[1]) - 1,
        date: parseInt(parts[2]),
    };
}

export function parseTimeStringForMoment(time: string): MomentTimeParts {
    const parts = time.split(":");
    return {
        hour: parseInt(parts[0]),
        minute: parseInt(parts[1]),
    };
}

export function areObjectsEqual(first: any, second: any): boolean {
    if (Object.getOwnPropertyNames(first).length !== Object.getOwnPropertyNames(second).length) {
        return false;
    }

    for (const key in first) {
        if (
            !Object.prototype.hasOwnProperty.call(first, key) ||
            !Object.prototype.hasOwnProperty.call(second, key) ||
            first[key] !== second[key]
        ) {
            return false;
        }
    }

    return true;
}

/**
 * Removes the "key" attributes from the COPY of the "obj" parameter
 * @param obj source object, remains untouched
 * @param key keyof obj
 * @return copy of the obj
 */
export function removeAttr<T>(obj: T, key: keyof T): T {
    const res = { ...obj };
    if (res[key] !== undefined) delete res[key];
    return res;
}

export const conferenceLocalStorage = createStorageStore(
    window.localStorage,
    "dvrt:conference:local",
    defaultConferenceLocalStorageSchema,
);
export const conferenceSessionStorage = createStorageStore(
    window.sessionStorage,
    "dvrt:conference:session",
    defaultConferenceSessionStorageSchema,
);

export const phoneClientLocalStorage = createStorageStore(
    window.localStorage,
    "dvrt:phone-client:local",
    defaultClientSettings,
);

export const ROOT_FIELD_NAME = "_Root__";

export function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function convertKeysToCamelCase(data: Record<string, any>): Record<string, any> {
    const res: Record<string, any> = {};
    for (const key of objectKeys(data)) {
        const newKey = key
            .split("_")
            .map((s) => capitalizeFirstLetter(s))
            .join("");
        const value = data[key];
        res[newKey.charAt(0).toLowerCase() + newKey.slice(1)] = isObject(value) ? convertKeysToCamelCase(value) : value;
    }
    return res;
}

const isObject = (a: any): a is Record<string, any> => {
    return !!a && a.constructor === Object;
};

export function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export const getBrowserIcon = (): IconName => {
    const browser = Bowser.getParser(window.navigator.userAgent);
    const info = browser.getBrowser();
    if (info.name?.toLowerCase() === "chrome") return "chrome";
    if (info.name?.toLowerCase() === "safari") return "safari";
    if (info.name?.toLowerCase() === "firefox") return "firefox";
    if (info.name?.toLowerCase().search("edge")) return "edge";
    return "internet-explorer";
};

export type KeysMatching<T, V> = {
    [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

export function hasSetsDiff<T>(a: T[], b: Set<T>): boolean {
    if (a.length !== b.size) return true;
    if (a.filter((x) => !b.has(x)).length) return true;
    return [...b].filter((x) => !a.includes(x)).length > 0;
}

export function difference<T>(setA: Set<T>, setB: Set<T>): Set<T> {
    const _difference = new Set(setA);
    for (const elem of setB) {
        _difference.delete(elem);
    }
    return _difference;
}

export function intersection<T>(setA: Set<T>, setB: Set<T>): Set<T> {
    const _intersection = new Set<T>();
    for (const elem of setB) {
        if (setA.has(elem)) {
            _intersection.add(elem);
        }
    }
    return _intersection;
}

export function generateRandomString(length: number): string {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

export const conferenceDebugSettingsStorage = createStorageStore(
    window.localStorage,
    "dvrt:conference:debugSettings",
    defaultDebugSettings,
    { migration: { legacyObjectKeys: ["debugSettings"] } },
);
export const {
    SettingsStoreProvider: DebugSettingsProvider,
    useSettings: useDebugSettings,
} = createSettingsStoreContext<DebugSettings>();

function getIsBrowserSupported(): boolean {
    const name = getBrowserIcon();
    const minVer = window.supportedBrowsers[name];

    if (minVer === undefined) return false;

    const ver = Bowser.getParser(window.navigator.userAgent).getBrowser().version;

    return ver ? compareVersions(ver, minVer) >= 0 : false;
}

export const isBrowserSupported = getIsBrowserSupported();

function getBrowserWithVersion() {
    const data = Bowser.getParser(window.navigator.userAgent);

    return `${data.getOSName()}/${data.getBrowserName()}/${data.getBrowserVersion()}`;
}

export const browserWithVersion: string = getBrowserWithVersion();

export const useResizeObserver = (
    elementRef: RefObject<HTMLElement>,
    onResize: (entry: ResizeObserverEntry) => void,
) => {
    useEffect(() => {
        const observer = new ResizeObserver((entries) => onResize(entries[0]));
        observer.observe(elementRef.current);
        return () => observer.disconnect();
    }, []);
};

// useful to avoid stale closures
export function useAsyncReference<T>(value: T): [RefObject<T>, (v: T) => void] {
    const ref = useRef<T>(value);
    const [, forceRender] = useState(false);

    function updateState(newState: T) {
        ref.current = newState;
        forceRender((s) => !s);
    }

    return [ref, updateState];
}

export function removeArrayItem<T>(source: T[], item: T): T[] {
    const newList = [...source];
    newList.splice(newList.indexOf(item), 1);
    return newList;
}

export function useChangeTo<T>(value: T, target: T, callback: () => void) {
    useEffect(() => {
        if (value === target) callback();
    }, [value]);
}

export function minMax(val: number, min: number, max: number): number {
    return Math.min(Math.max(val, min), max);
}
